<template>
  <div class="navigation">
    <span
      id="navigation-title"
      class="visually-hidden"
      v-text="$string('general.site_header.navigation')"
    />

    <nav class="navigation__nav">
      <ul class="navigation__list">
        <template
          v-for="tier1 of desktop.items"
          :key="tier1.handle"
        >
          <li class="navigation__item" :class="{ 'navigation__item--opened': megamenu }">
            <button
              v-if="parentSettings.enableMegamenu && hasMegamenuBlock(tier1.title)"
              type="button"
              class="navigation__link text-body-m-bold"
              :class="{ [$class('active')]: activeMenuItem && activeMenuItem.title === tier1.title }"
              @click="handleMegamenu(tier1)"
              v-text="tier1.title"
            />
            <a
              v-else
              class="navigation__link text-body-m-bold"
              :href="tier1.url"
              v-text="tier1.title"
            />
          </li>
        </template>
      </ul>
    </nav>

    <teleport to="body">
      <overlay
        key="megamenuOverlay"
        direction="right"
        namespace="megamenuOverlay"
        :template="false"
        type="drawer"
        block-class="megamenu"
      >
        <template #body>
          <div class="megamenu">
            <template v-if="megamenu">
              <button
                type="button"
                class="megamenu__close"
                @click="closeOverlay('megamenuOverlay')"
              >
                <span
                  class="visually-hidden"
                  v-text="$string('accessibility.close')"
                />

                <icon-close />
              </button>

              <nav class="megamenu__list">
                <ul class="megamenu__parent">
                  <li
                    v-for="(item, index) of desktop.items"
                    :key="index"
                    class="megamenu__parent-item"
                  >
                    <button
                      v-if="item.children"
                      class="text-body-m-bold"
                      :class="{ [$class('active')]: megamenu && megamenu.title === item.title }"
                      type="button"
                      @mouseenter="handleMegamenu(item)"
                      v-text="item.title"
                    />

                    <a
                      v-else
                      class="text-body-m-bold"
                      :class="{ [$class('active')]: megamenu && megamenu.title === item.title }"
                      :href="item.url"
                      v-text="item.title"
                    />
                  </li>
                </ul>

                <ul class="megamenu__link-wrapper">
                  <li
                    v-for="link of megamenu.children"
                    :key="link.url"
                    class="megamenu__item"
                  >
                    <a
                      class="megamenu__link text-heading-xl"
                      :href="link.url"
                      @mouseenter="hoverMedia.src = link.title.split(' | ')[1] || null"
                      @mouseleave="hoverMedia.src = null"
                      v-text="link.title.split(' | ')[0]"
                    />
                  </li>
                </ul>
              </nav>

              <media-block
                v-if="megamenu.media"
                :media="megamenu.media"
                :ratio="0.58"
                block-class="megamenu"
                :custom-image="hoverMedia"
              />
            </template>
          </div>
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>

/**
 * Vue: Navigation (site-header)
 * -----------------------------------------------------------------------------
 * Desktop navigation links.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} desktop - Desktop linklist handle and items.
 *
 */

import { mapActions } from 'vuex'
import MediaBlock from '~async/menu-drawer/media-block'
import Overlay from '~global/overlay/overlay'
import IconClose from '~icons/directional-navigation/close.svg'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Navigation',

  components: {
    Overlay,
    IconClose,
    MediaBlock,
  },

  props: {
    desktop: {
      type: Object,
      default: () => ({
        handle: '',
        items: () => [],
      }),
    },
    parentSettings: {
      type: Object,
      default: () => ({
        enableMegamenu: true,
        megamenuStyle: 'shelf',
        megamenuColumns: 4
      })
    },
    blocks: {
      type: Array,
      default: () => []
    }
  },

  data: () => {
    return {
      megamenu: null,
      activeMenuItem: null,
      hoverMedia: {
        src: null,
        width: 525,
        height: 900,
      },
    }
  },

  methods: {
    /**
     * Check if a menu item has a mega menu block.
     * @param {string} title - Menu item title to check
     * @returns {boolean} Whether the menu item has a mega menu block
     */
    hasMegamenuBlock(title) {
      return this.blocks.some(block =>
        block.type === 'navigation' &&
        block.settings.handle === title
      )
    },

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * Open corresponding megamenu based on menu item.
     * @param {Object} menuItem - Menu item with children
     */
    handleMegamenu(menuItem) {
      if (!this.parentSettings.enableMegamenu) return

      // Set active menu item
      this.activeMenuItem = menuItem

      // First open the overlay
      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'megamenuShelf',
      })

      // Then emit the event with menu data
      this.$emit('handle-megamenu', {
        title: menuItem.title,
        url: menuItem.url,
        style: this.parentSettings.megamenuStyle,
        columns: this.parentSettings.megamenuColumns,
        children: menuItem.children || [],
      })
    },
  },
}
</script>
